import type { ListingFacetCategoryData } from "src/types/adapters/listing.type";
import type { CategoryNodeType } from "src/molecules/CategoryTree/types";
import type { TreeType } from "src/types/Tree";
import type { CategoryFilterOptionOrder } from "src/types/graphql.d";

export const ROOT_PARENT_ID = "0";

export function getChildrenSlugs(
  tree: TreeType<CategoryNodeType>,
  rootItemId: string
): string[] {
  const childrenByParent = new Map<string, CategoryNodeType[]>();

  for (const node of tree) {
    const list = childrenByParent.get(node.parentId);

    if (list) {
      list.push(node);
    } else {
      childrenByParent.set(node.parentId, [node]);
    }
  }

  const result: string[] = [];
  const stack = [rootItemId];

  while (stack.length > 0) {
    const id = stack.pop() as string;

    for (const child of childrenByParent.get(id) || []) {
      result.push(child.slug);
      stack.push(child.itemId);
    }
  }

  return result;
}

export function omitParentSlugs(
  selectedSlugs: string[],
  tree: TreeType<CategoryNodeType>
): string[] {
  if (selectedSlugs.length <= 1 || tree.length === 0) {
    return selectedSlugs;
  }

  const selectedSet = new Set(selectedSlugs);
  const itemIdBySlug = new Map<string, string>();

  for (const node of tree) {
    itemIdBySlug.set(node.slug, node.itemId);
  }

  return selectedSlugs.filter((slug) => {
    const itemId = itemIdBySlug.get(slug);

    if (!itemId) {
      return true;
    }

    const descendants = getChildrenSlugs(tree, itemId);
    return !descendants.some((descendant) => selectedSet.has(descendant));
  });
}

export function buildCategoryTree(
  data: ListingFacetCategoryData[]
): TreeType<CategoryNodeType> {
  const nodes = new Map<string, CategoryNodeType>();

  for (const item of data) {
    if (!item.id || nodes.has(item.id)) {
      continue;
    }

    nodes.set(item.id, {
      itemId: item.id,
      parentId: item.parent || ROOT_PARENT_ID,
      label: item.label,
      value: item.value,
      slug: item.slug,
    });
  }

  return Array.from(nodes.values());
}

export function getSubcategories(
  categories: ListingFacetCategoryData[],
  parentId: string,
  {
    gender,
    sortOrder = [],
  }: { gender?: string; sortOrder?: CategoryFilterOptionOrder[] } = {}
): ListingFacetCategoryData[] {
  const positionBySlug = new Map(
    sortOrder.map(({ slug, order }) => [slug, order])
  );
  const position = (slug: string) => positionBySlug.get(slug) ?? Infinity;

  return categories
    .filter(
      (item) =>
        item.parent === parentId && (!gender || item.genders.includes(gender))
    )
    .sort(
      (a, b) =>
        position(a.slug) - position(b.slug) || a.label.localeCompare(b.label)
    );
}
